import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Users, Clock, Heart, TrendingUp, MessageCircle, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { pricingPlans, faqs, howItWorks, whyChooseUs } from '../mock';

const Home = () => {
  const iconMap = {
    Users: Users,
    BookOpen: BookOpen,
    Clock: Clock,
    Heart: Heart,
    TrendingUp: TrendingUp,
    MessageCircle: MessageCircle,
    CheckCircle: CheckCircle
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            Aiutiamo i tuoi figli a studiare con serenità e successo
          </h1>
          <p className="hero-subtitle">
            StudioInsieme è un servizio educativo di doposcuola per studenti di elementari e medie: supporto compiti, metodo di studio, autonomia e più serenità a casa. Online e in presenza.
          </p>
          <div className="hero-actions">
            <Button asChild size="lg" className="btn-primary">
              <Link to="/iscrizione">
                Iscriviti ora <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="btn-secondary">
              <Link to="/contatti">Richiedi informazioni</Link>
            </Button>
          </div>
          <div className="hero-image-container">
            <img 
              src="https://images.pexels.com/photos/8423410/pexels-photo-8423410.jpeg?w=1200&q=80" 
              alt="Tre ragazze che studiano insieme"
              className="hero-image"
            />
          </div>
        </div>
      </section>

      {/* Come Funziona */}
      <section className="section-spacing">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Come funziona</h2>
            <p className="body-large text-secondary">
              Un percorso semplice e chiaro per iniziare subito
            </p>
          </div>
          <div className="ai-grid">
            {howItWorks.map((step) => (
              <Card key={step.step} className="product-card hover-card">
                <CardHeader>
                  <div className="step-number">{step.step}</div>
                  <CardTitle className="product-card-title">{step.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="product-card-description">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="section-image-container">
            <img 
              src="https://images.unsplash.com/photo-1623076189461-f7706b741c04?w=1000&q=80" 
              alt="Bambina che studia felice"
              className="section-image"
            />
          </div>
        </div>
      </section>

      {/* Perché Sceglierci */}
      <section className="section-spacing bg-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Perché scegliere StudioInsieme</h2>
            <p className="body-large text-secondary">
              Non solo compiti: un vero supporto educativo personalizzato
            </p>
          </div>
          <div className="ai-grid">
            {whyChooseUs.map((item) => {
              const IconComponent = iconMap[item.icon] || CheckCircle;
              return (
                <Card key={item.title} className="product-card hover-card">
                  <CardHeader>
                    <div className="icon-wrapper">
                      <IconComponent className="feature-icon" />
                    </div>
                    <CardTitle className="product-card-title">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="product-card-description">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="section-image-container">
            <img 
              src="https://images.unsplash.com/photo-1588075592446-265fd1e6e76f?w=1000&q=80" 
              alt="Classe con studenti e insegnante"
              className="section-image"
            />
          </div>
        </div>
      </section>


      {/* Chiarezza servizio */}
      <section className="home-compliance-section">
        <div className="container">
          <div className="compliance-grid">
            <div className="compliance-copy">
              <span className="section-badge">Cosa acquistano le famiglie</span>
              <h2>Supporto scolastico chiaro, umano e adatto al bisogno dello studente</h2>
              <p>
                Non vendiamo lo svolgimento dei compiti al posto dei ragazzi. Offriamo doposcuola, supporto didattico,
                metodo di studio e accompagnamento all'autonomia per bambini e ragazzi di scuola primaria e secondaria
                di primo grado.
              </p>
              <p>
                Prima dell'avvio raccogliamo informazioni su classe, materie, difficoltà, obiettivi della famiglia,
                modalità online o in presenza e disponibilità. Per situazioni più complesse facciamo una call conoscitiva
                con il genitore e, quando utile, con lo studente.
              </p>
            </div>
            <div className="compliance-panel">
              <h3>Incluso nel percorso</h3>
              <ul>
                <li>Supporto compiti e studio per elementari e medie</li>
                <li>Metodo, organizzazione e maggiore autonomia</li>
                <li>Confronto con i genitori e monitoraggio dei progressi</li>
                <li>Supporto didattico per DSA/BES e difficoltà di attenzione o organizzazione</li>
                <li>Pacchetti personalizzati solo dopo call e conferma del preventivo</li>
              </ul>
              <p className="compliance-note">
                Il servizio è educativo e didattico: non effettuiamo diagnosi, certificazioni, terapia o prestazioni sanitarie.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Prezzi */}
      <section className="section-spacing">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Piani semplici e trasparenti</h2>
            <p className="body-large text-secondary">
              Scegli il piano più adatto alle esigenze della tua famiglia
            </p>
          </div>
          <div className="pricing-grid">
            {pricingPlans.map((plan) => (
              <Card 
                key={plan.id} 
                className={`pricing-card ${plan.popular ? 'pricing-card-popular' : ''}`}
              >
                {plan.popular && (
                  <Badge className="popular-badge">Più scelto</Badge>
                )}
                <CardHeader>
                  <CardTitle className="heading-3">{plan.name}</CardTitle>
                  <CardDescription className="body-medium">{plan.description}</CardDescription>
                  <div className="price-container">
                    <span className="price">€{plan.price}</span>
                    <span className="price-period">/{plan.period}</span>
                  </div>
                  {plan.savings && (
                    <p className="savings-text">{plan.savings}</p>
                  )}
                </CardHeader>
                <CardContent>
                  <ul className="feature-list">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="feature-item">
                        <CheckCircle className="check-icon" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button asChild className={plan.popular ? 'btn-primary w-full' : 'btn-secondary w-full'}>
                    <Link to={plan.requiresCall ? `/pagamento?plan=${plan.id}` : "/iscrizione"}>
                      {plan.requiresCall ? "Vai al pagamento" : "Scegli questo piano"}
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Chi Siamo */}
      <section className="section-spacing bg-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Chi siamo</h2>
          </div>
          <div className="chi-siamo-content">
            <div className="chi-siamo-text">
              <p className="body-large">
                StudioInsieme nasce dalla passione per l'educazione e dalla consapevolezza che ogni studente ha bisogno di un supporto personalizzato per esprimere il proprio potenziale.
              </p>
              <p className="body-medium text-secondary">
                Il nostro team è formato da educatori qualificati, con esperienza nel sostegno scolastico e nella didattica personalizzata. Crediamo che studiare non debba essere fonte di stress, ma un'opportunità di crescita e scoperta.
              </p>
              <p className="body-medium text-secondary">
                Lavoriamo ogni giorno con impegno, pazienza e dedizione per aiutare i ragazzi a diventare più autonomi, sicuri e organizzati nello studio. Per noi il successo scolastico è il risultato di un percorso fatto insieme, con il coinvolgimento della famiglia e il rispetto dei tempi di ogni studente.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bisogni frequenti */}
      <section className="section-spacing">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Per quali bisogni ci contattano le famiglie</h2>
            <p className="body-large text-secondary">
              Situazioni concrete che valutiamo prima di proporre il percorso piu adatto
            </p>
          </div>
          <div className="ai-grid">
            <Card className="testimonial-card">
              <CardHeader>
                <CardTitle className="heading-3">Compiti che creano tensione</CardTitle>
                <CardDescription className="caption">Supporto scolastico</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="body-medium text-secondary">
                  Aiutiamo quando i compiti diventano fonte di conflitto in casa e serve una figura esterna che accompagni lo studio con calma e metodo.
                </p>
              </CardContent>
            </Card>
            <Card className="testimonial-card">
              <CardHeader>
                <CardTitle className="heading-3">Metodo e organizzazione</CardTitle>
                <CardDescription className="caption">Autonomia nello studio</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="body-medium text-secondary">
                  Lavoriamo su agenda, priorita, preparazione delle verifiche, gestione del materiale e strategie pratiche per studiare meglio.
                </p>
              </CardContent>
            </Card>
            <Card className="testimonial-card">
              <CardHeader>
                <CardTitle className="heading-3">Bisogni specifici DSA/BES</CardTitle>
                <CardDescription className="caption">Solo ambito educativo</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="body-medium text-secondary">
                  Possiamo offrire supporto didattico mirato per difficolta di lettura, scrittura, calcolo, attenzione o organizzazione, senza diagnosi o prestazioni sanitarie.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-spacing bg-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Domande frequenti</h2>
            <p className="body-large text-secondary">
              Risposte chiare alle domande più comuni
            </p>
          </div>
          <div className="faq-container">
            <Accordion type="single" collapsible className="faq-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem key={faq.question} value={`item-${index}`}>
                  <AccordionTrigger className="faq-question">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="faq-answer">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Finale */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="heading-2">Inizia oggi il percorso verso il successo scolastico</h2>
            <p className="body-large">
              Iscriviti ora e dai a tuo figlio il supporto che merita
            </p>
            <div className="cta-actions">
              <Button asChild size="lg" className="btn-primary">
                <Link to="/iscrizione">
                  Iscriviti al doposcuola <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="btn-secondary">
                <Link to="/contatti">Contattaci</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
